/**
 * Client-side live preview for TYPO3 EXT:form file/image upload elements.
 *
 * Applies to any `<input type="file">` carrying the "js-form-upload-preview" marker class
 * (added by the "ImageUploadWithPreview" form element, and by the enhanced "FileUpload" partials).
 * The generated markup mirrors what EXT:form itself renders after a successful upload + redisplay:
 * - images: `<div id="{id}-preview"><a class="..." href="..."><img src="..." alt="..."></a></div>`
 * - other files: `<div id="{id}-preview">{filename}</div>`
 */

const objectUrlsByInput = new WeakMap<HTMLInputElement, string>();

const revokeTrackedObjectUrl = (input: HTMLInputElement): void => {
	const objectUrl = objectUrlsByInput.get(input);

	if (objectUrl === undefined) {
		return;
	}
	URL.revokeObjectURL(objectUrl);
	objectUrlsByInput.delete(input);
};

const getOrCreatePreviewElement = (input: HTMLInputElement): HTMLDivElement => {
	const previewId = `${input.id}-preview`;
	const existingPreview = document.getElementById(previewId);

	if (existingPreview instanceof HTMLDivElement) {
		return existingPreview;
	}

	const preview = document.createElement('div');

	preview.id = previewId;
	input.insertAdjacentElement('beforebegin', preview);

	return preview;
};

const renderImagePreview = (preview: HTMLDivElement, input: HTMLInputElement, file: File, objectUrl: string): void => {
	const link = document.createElement('a');

	link.className = input.dataset.elementClass ?? '';
	link.href = objectUrl;

	const image = document.createElement('img');

	image.src = objectUrl;
	image.alt = file.name;

	// Caps the preview to the same dimensions EXT:form uses for the server-processed image
	const maxWidth = Number(input.dataset.imageMaxWidth);
	const maxHeight = Number(input.dataset.imageMaxHeight);

	if (Number.isFinite(maxWidth) && (maxWidth > 0)) {
		image.style.maxWidth = `${maxWidth}px`;
	}
	if (Number.isFinite(maxHeight) && (maxHeight > 0)) {
		image.style.maxHeight = `${maxHeight}px`;
	}
	link.replaceChildren(image);
	preview.replaceChildren(link);
};

const renderFileNamePreview = (preview: HTMLDivElement, file: File): void => {
	preview.replaceChildren(
		document.createTextNode(file.name),
	);
};

const handleFileInputChange = (input: HTMLInputElement): void => {
	revokeTrackedObjectUrl(input);

	const file = input.files?.[0];

	if (file === undefined) {
		document.getElementById(`${input.id}-preview`)
			?.remove();

		return;
	}

	const objectUrl = URL.createObjectURL(file);

	objectUrlsByInput.set(input, objectUrl);

	const preview = getOrCreatePreviewElement(input);

	if (file.type.startsWith('image/')) {
		renderImagePreview(preview, input, file, objectUrl);
	} else {
		renderFileNamePreview(preview, file);
	}
};

const initializeUploadPreviews = (): void => {
	document.querySelectorAll<HTMLInputElement>('input[type="file"].js-form-upload-preview')
		.forEach((input) => {
			input.addEventListener('change', () => handleFileInputChange(input));
		});
};

// The module is loaded with "async", so it can run before the rest of the document has been parsed
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initializeUploadPreviews);
} else {
	initializeUploadPreviews();
}
